import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { COMPANY_CONFIG } from '../config/config';
import LoginModal from './LoginModal';
import OtpLoginModal from './OtpLoginModal';
import PasswordResetModal from './PasswordResetModal';
import WebsiteLoading from './WebsiteLoading';
import { 
  Menu, 
  X, 
  Home, 
  Bus, 
  MapPin, 
  Calendar, 
  Users, 
  LogOut,
  User,
  Phone,
  Mail,
  Plus,
  Database,
  Ticket,
  Building2,
  ChevronDown,
  Key,
  UserCircle
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout, isRole, ROLES, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [passwordResetModalOpen, setPasswordResetModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Listen for token expiration events to show OTP modal
  useEffect(() => {
    const handleTokenExpired = () => {
      console.log('🔔 Token expired event received - opening OTP modal');
      // Show OTP modal when token expires - open immediately
      setOtpModalOpen(true);
    };

    window.addEventListener('auth:token-expired', handleTokenExpired);
    return () => {
      window.removeEventListener('auth:token-expired', handleTokenExpired);
    };
  }, []);

  // Handle OTP login success
  const handleOtpSuccess = () => {
    setOtpModalOpen(false);
  };

  // Role-based navigation
  const getNavigation = () => {
    if (!user) {
      return [
        { name: 'Home', href: '/', icon: Home },
        { name: 'Book Tickets', href: '/book', icon: Bus },
        // { name: 'Our Routes', href: '/routes', icon: MapPin }, // Hidden - uncomment to show
        { name: 'Schedules', href: '/schedules', icon: Calendar },
        { name: 'Contact', href: '/contact', icon: Phone },
      ];
    }

    if (isRole(ROLES.MANAGER)) {
      return [
        { name: 'Dashboard', href: '/manager', icon: Home },
        { name: 'Admin Booking', href: '/admin/booking', icon: Bus },
        { name: 'Vehicles', href: '/vehicles', icon: Bus },
        { name: 'Routes', href: '/route-management', icon: MapPin },
        { name: 'Schedules', href: '/schedule-management', icon: Calendar },
        { name: 'Cities', href: '/city-management', icon: Building2 }
      ];
    }

    if (isRole(ROLES.PASSENGER)) {
      return [
        { name: 'Home', href: '/', icon: Home },
        { name: 'Book Tickets', href: '/book', icon: Bus },
        // { name: 'Our Routes', href: '/routes', icon: MapPin }, // Hidden - uncomment to show
        { name: 'Schedules', href: '/schedules', icon: Calendar },
        { name: 'My Bookings', href: '/bookings', icon: Ticket },
        { name: 'Contact', href: '/contact', icon: Phone },
      ];
    }

    if (isRole(ROLES.CONDUCTOR)) {
      return [
        { name: 'Seat Status', href: '/seat-status', icon: Bus },
      ];
    }

    return [];
  };

  const navigation = getNavigation();

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    setSidebarOpen(false);
    setUserDropdownOpen(false);
    // Redirect to landing page after logout
    navigate('/');
  };

  const handlePasswordReset = () => {
    setPasswordResetModalOpen(true);
    setUserDropdownOpen(false);
  };

  const handleUserDropdownToggle = () => {
    setUserDropdownOpen(!userDropdownOpen);
  };

  // Close dropdown when clicking outside
  const handleClickOutside = (e) => {
    if (userDropdownOpen && !e.target.closest('.user-dropdown')) {
      setUserDropdownOpen(false);
    }
  };

  // Add event listener for clicking outside
  React.useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userDropdownOpen]);

  // Show website loading while app is initializing
  if (loading) {
    return <WebsiteLoading />;
  }

  return (
    <div className="min-h-screen w-screen min-w-screen max-w-screen bg-gray-50 overflow-x-hidden">
      {/* Header */}
      <header className="bg-white shadow-lg border-b border-gray-200 fixed top-0 left-0 right-0 z-50 w-screen min-w-screen max-w-screen">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 lg:h-20">
            {/* Logo */}
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Link to="/" className="flex flex-col items-center">
                  <img 
                    src="/images/tripimate-01.png" 
                    alt="tripimate" 
                    className="h-10 lg:h-12 object-contain"
                  />
                  <div className="text-xs lg:text-sm text-gray-600">
                    {COMPANY_CONFIG.taglinelogo}
                  </div>
                </Link>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-8">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`inline-flex items-center px-3 py-2 text-sm lg:text-base font-medium border-b-2 transition-all duration-200 ${
                      isActive(item.href)
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4 lg:w-5 lg:h-5 mr-2" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>

            {/* User Menu */}
            <div className="hidden lg:flex items-center space-x-6">
              {user ? (
                <div className="relative user-dropdown">
                  <button
                    onClick={handleUserDropdownToggle}
                    className="flex items-center space-x-3 bg-gray-50 hover:bg-gray-100 rounded-xl px-4 py-2 transition-colors"
                  >
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium text-gray-900">{user.fullName || user.name || user.username}</div>
                      <div className="text-xs text-gray-500">{user.roleName}</div>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${userDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {userDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                      {/* User Info */}
                      <div className="px-4 py-3 border-b border-gray-100">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{user.fullName || user.name || user.username}</div>
                            <div className="text-sm text-gray-500">{user.email || user.username || 'No email'}</div>
                            <div className="text-xs text-blue-600 font-medium">{user.roleName}</div>
                          </div>
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="py-2">
                        {user && !isRole(ROLES.PASSENGER) && (
                        <button
                          onClick={handlePasswordReset}
                          className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <Key className="w-4 h-4" />
                          <span>Reset Password</span>
                        </button>
                        )}
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <Phone className="w-4 h-4" />
                    <span>{COMPANY_CONFIG.phone}</span>
                  </div>
                  <button
                    onClick={() => setLoginModalOpen(true)}
                    className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    Sign In
                  </button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setSidebarOpen(false)}
          />
          
          {/* Sidebar */}
          <div className="fixed right-0 top-0 h-full w-80 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Menu</h2>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {/* Navigation */}
              <nav className="flex-1 px-6 py-4">
                <div className="space-y-2">
                  {navigation.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 ${
                          isActive(item.href)
                            ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-500'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <Icon className="w-5 h-5 mr-3" />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </nav>
              
              {/* Footer */}
              <div className="p-6 border-t border-gray-200">
                {user ? (
                  <div className="space-y-4">
                    {/* User Info */}
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-3 mb-3">
                        <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                          <User className="w-6 h-6 text-white" />
                        </div>
                        <div className="text-left">
                          <div className="font-medium text-gray-900">{user.fullName || user.name || user.username}</div>
                          <div className="text-sm text-gray-500">{user.email || user.username || 'No email'}</div>
                        </div>
                      </div>
                      <div className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
                        {user.roleName}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-3">
                      {user && !isRole(ROLES.PASSENGER) && (
                      <button
                        onClick={handlePasswordReset}
                        className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
                      >
                        <Key className="w-4 h-4" />
                        <span>Reset Password</span>
                      </button>
                      )}
                      <button
                        onClick={handleLogout}
                        className="w-full bg-red-600 text-white px-6 py-3 rounded-xl hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 mb-3">
                      <Phone className="w-4 h-4" />
                      <span>{COMPANY_CONFIG.phone}</span>
                    </div>
                    <button
                      onClick={() => {
                        setLoginModalOpen(true);
                        setSidebarOpen(false);
                      }}
                      className="w-full bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors"
                    >
                      Sign In
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="w-screen min-w-screen max-w-screen min-h-screen pt-16 lg:pt-20">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white w-screen min-w-screen max-w-screen">
        <div className="w-full py-12 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <img 
                  src="/images/tripimate-03.png" 
                  alt="tripimate" 
                  className="h-8 lg:h-10 mr-3 object-contain"
                />
              
              </div>
              <p className="text-gray-300 mb-6 text-lg leading-relaxed">
                Through this service long distance travelers could be able to book any bus seats 
                for anywhere in Sri Lanka at one place.
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-6 text-base text-gray-300">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-3" />
                  <span>{COMPANY_CONFIG.phone}</span>
                </div>
                <div className="flex items-center">
                  <Mail className="w-5 h-5 mr-3" />
                  <span>{COMPANY_CONFIG.email}</span>
                </div>
              </div>
            </div>

            {/* Useful Links */}
            <div>
              <h3 className="text-xl font-semibold mb-6">Useful Links</h3>
              <ul className="space-y-3">
                <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors text-lg">About</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white transition-colors text-lg">Contact</Link></li>
                <li><Link to="/routes" className="text-gray-300 hover:text-white transition-colors text-lg">Bus Routes</Link></li>
                <li><Link to="/schedules" className="text-gray-300 hover:text-white transition-colors text-lg">Schedules</Link></li>
              </ul>
            </div>

            {/* Policies */}
            <div>
              <h3 className="text-xl font-semibold mb-6">Policies</h3>
              <ul className="space-y-3">
                <li><Link to="/privacy" className="text-gray-300 hover:text-white transition-colors text-lg">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-300 hover:text-white transition-colors text-lg">Terms & Conditions</Link></li>
                <li><Link to="/ticket-policy" className="text-gray-300 hover:text-white transition-colors text-lg">Ticket Policies</Link></li>
              </ul>
            </div>
          </div>
          
          {/* Payment Methods */}
          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="text-center mb-6">
              <div className="flex flex-wrap items-center justify-center gap-4 lg:gap-6">
                <img 
                  src="/images/1.jpg" 
                  alt="Payment Method" 
                  className="h-10 lg:h-12 object-contain opacity-90 hover:opacity-100 transition-opacity"
                />
                <img 
                  src="/images/visa.png" 
                  alt="Visa" 
                  className="h-10 lg:h-12 object-contain opacity-90 hover:opacity-100 transition-opacity"
                />
                <img 
                  src="/images/master.png" 
                  alt="Mastercard" 
                  className="h-10 lg:h-12 object-contain opacity-90 hover:opacity-100 transition-opacity"
                />
                <img 
                  src="/images/master%20card%20secure%20gate.jpg" 
                  alt="Mastercard Secure Gate" 
                  className="h-10 lg:h-12 object-contain opacity-90 hover:opacity-100 transition-opacity"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    console.error('Failed to load image: master card secure gate.jpg');
                  }}
                />
                <img 
                  src="/images/Verifid%20by%20visa.jpg" 
                  alt="Verified by Visa" 
                  className="h-10 lg:h-12 object-contain opacity-90 hover:opacity-100 transition-opacity"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    console.error('Failed to load image: Verifid by visa.jpg');
                  }}
                />
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p className="text-lg">&copy; 2024 {COMPANY_CONFIG.name}. All rights reserved.</p>
            <p className="text-base mt-2">
              Design and Developed by{' '}
              <a 
                href="https://www.visualsoftsolution.lk/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline transition-colors"
              >
                VSS
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      <LoginModal isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
      
      {/* OTP Login Modal - Shown when token expires */}
      <OtpLoginModal 
        isOpen={otpModalOpen} 
        onClose={() => setOtpModalOpen(false)} 
        onSuccess={handleOtpSuccess}
        showExpiredMessage={true}
      />
      
      {/* Password Reset Modal */}
      <PasswordResetModal 
        isOpen={passwordResetModalOpen} 
        onClose={() => setPasswordResetModalOpen(false)} 
        userId={user?.userID || user?.id || user?.userId}
      />
    </div>
  );
};

export default Layout; 